<script setup>
import { computed, ref, nextTick, onMounted, watch } from 'vue';
import { useChatStore } from '../../stores/chat';
import QuotaUsage from './QuotaUsage.vue';

const chatStore = useChatStore();
const textareaRef = ref(null);

// Auto focus textarea when component is mounted
onMounted(() => {
  nextTick(() => {
    if (textareaRef.value && chatStore.settings.hasApiKey) {
      textareaRef.value.focus();
    }
  });
});

// Watch for isSending changes and refocus when sending is complete
watch(
  () => chatStore.isSending,
  (newValue, oldValue) => {
    // When sending finishes (from true to false), refocus textarea
    if (oldValue === true && newValue === false) {
      nextTick(() => {
        if (textareaRef.value && chatStore.settings.hasApiKey) {
          textareaRef.value.focus();
        }
      });
    }
  }
);

const canSend = computed(() => {
  return chatStore.currentMessage.trim() !== '' && !chatStore.isSending && chatStore.settings.hasApiKey;
});

const handleSend = () => {
  if (canSend.value) {
    // Preserve newlines by replacing them with <br> tags
    const trimmedMessage = chatStore.currentMessage.replace(/^\n+/, '');
    const formattedMessage = trimmedMessage.replace(/\n/g, '<br>');
    
    chatStore.sendMessage({
      original: chatStore.currentMessage.trim(),
      formatted: formattedMessage
    });
    
    chatStore.clearCurrentMessage();
    
    // Focus will be handled by the watcher when isSending becomes false
  }
};

const handleKeyDown = (event) => {
  // Check if Enter key is pressed without Shift key
  if (event.keyCode === 13 && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
};
</script>

<template>
  <div class="dataagent-ai-input-area da:bg-gray-100 da:border-t da:border-gray-200 da:p-4">
    <!-- Textarea -->
    <textarea 
      ref="textareaRef"
      v-model="chatStore.currentMessage"
      @keydown="handleKeyDown"
      class="dataagent-ai-textarea da:w-full da:p-3 da:border da:border-gray-300 da:rounded-lg da:focus:ring-2 da:focus:ring-green-500 da:focus:border-green-500 da:resize-none da:mb-3"
      :class="{ 'da:opacity-50 da:cursor-not-allowed': !chatStore.settings.hasApiKey || chatStore.isSending }"
      :placeholder="chatStore.labels.type_message || 'Type your question here...'"
      rows="3"
      :disabled="!chatStore.settings.hasApiKey || chatStore.isSending"
    ></textarea>

    <!-- Action Buttons -->
    <div class="dataagent-ai-actions da:flex da:justify-between da:items-center da:mt-3">
      <!-- Quota Usage -->
      <div>
        <QuotaUsage v-if="!chatStore.quota.isPremium" />
      </div>

      <!-- Send Button -->
      <button 
        type="button" 
        @click="handleSend"
        class="dataagent-ai-button dataagent-ai-send-button button-primary da:cursor-pointer da:flex da:items-center da:px-6 da:py-2 da:rounded-lg da:transition-colors"
        :class="{
          'da:bg-green-600 da:text-white da:hover:bg-green-700': canSend,
          'da:opacity-50 da:cursor-not-allowed da:bg-gray-400 da:text-white': !canSend
        }"
        :disabled="!canSend"
      >
        <!-- Loading Spinner -->
        <template v-if="chatStore.isSending">
          <span class="da:mr-2">
            <svg class="da:w-4 da:h-4 da:animate-spin" viewBox="0 0 24 24">
              <circle class="da:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
              <path class="da:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
            </svg>
          </span>
        </template>
        <!-- Send Icon -->
        <template v-else>
          <span class="dashicons dashicons-arrow-right-alt2 da:mr-1"></span>
        </template>
        {{ chatStore.isSending ? (chatStore.labels.sending || 'Sending...') : (chatStore.labels.send || 'Send') }}
      </button>
    </div>
  </div>
</template>